/**
 * M16CustomerDetailScreen - Chi tiết khách hàng
 * Trang 360° view của khách hàng với:
 * - Header + profile (avatar, tên, info)
 * - Tabs: Thông tin | Minh họa | Ghi chú | Người phụ thuộc
 * - Action bar: Tạo minh họa mới + Chỉnh sửa
 *
 * Pattern: Hợp nhất data từ M11 (illustrations) + M14 (notes) + M15 (dependents)
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { EditModal } from '../components/EditModal';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { QuickNoteModal } from '../components/QuickNoteModal';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import {
  findCustomerById,
  buildCustomerEditFields,
  type CustomerEditableFields,
  type CustomerIllustration,
  type CustomerNote,
  type CustomerFull,
} from '../data/customers';
import { LABEL_META, type CustomerLabel } from './M15CustomerListScreen';
import { customerRepository, noteRepository, illustrationRepository } from '../repositories';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M16CustomerDetailScreen'>;
type RouteParams = RouteProp<RootStackParamList, 'M16CustomerDetailScreen'>;

const STAGE_META: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'Mới tạo', color: '#2563EB', bg: '#EFF6FF' },
  negotiating: { label: 'Đang tư vấn', color: '#D97706', bg: '#FFFBEB' },
  closed: { label: 'Đã chốt', color: '#00A651', bg: '#F0FDF4' },
};

const NOTE_TYPE_META: Record<CustomerNote['type'], { label: string; icon: any; color: string; bg: string }> = {
  journal: { label: 'Nhật ký tư vấn', icon: 'description', color: '#003366', bg: 'rgba(0, 51, 102, 0.08)' },
  gift: { label: 'Tặng quà chăm sóc', icon: 'card_giftcard', color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
  claims: { label: 'Hỗ trợ sự kiện BH', icon: 'medical_services', color: '#E11D48', bg: 'rgba(225, 29, 72, 0.08)' },
};

const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'Vợ/Chồng',
  child: 'Con',
  parent: 'Bố/Mẹ',
  other: 'Khác',
};

export const M16CustomerDetailScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteParams>();
  const customerId = route.params?.customerId ?? '';

  const [customer, setCustomer] = useState<CustomerFull | undefined>(() => findCustomerById(customerId));

  const [activeTab, setActiveTab] = useState(0);
  const [editingCustomer, setEditingCustomer] = useState<CustomerEditableFields | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [repoNotes, setRepoNotes] = useState<any[]>([]);
  const [repoIllustrations, setRepoIllustrations] = useState<any[]>([]);

  useEffect(() => {
    if (customerId) {
      customerRepository.getById(customerId).then((c: any) => {
        if (c) {
          setCustomer({
            ...c,
            illustrations: [],
            notes: [],
            dependents: [],
          });
        }
      });
      noteRepository.getByCustomerId(customerId).then(setRepoNotes);
      illustrationRepository.getByCustomerId(customerId).then(setRepoIllustrations);
    }
  }, [customerId]);

  if (!customer) {
    return (
      <View style={styles.notFound}>
        <Icon name="error" size={48} color={colors.onSurfaceVariant} />
        <Text style={styles.notFoundText}>Không tìm thấy khách hàng: {customerId}</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Text style={styles.backBtnText}>Quay lại</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const TABS = [
    { key: 'info', label: 'Thông tin', icon: 'person' as const, count: null },
    { key: 'illustrations', label: 'Minh họa', icon: 'description' as const, count: customer.illustrations.length },
    { key: 'notes', label: 'Ghi chú', icon: 'comment' as const, count: customer.notes.length },
    { key: 'dependents', label: 'Người phụ thuộc', icon: 'groups' as const, count: customer.dependents.length },
  ];

  const handleViewIllustration = (i: CustomerIllustration) => {
    navigation.navigate('M07ResultScreen', {
      illustrationId: i.id,
      customerName: i.customerName,
      customerAge: i.customerAge,
      customerGender: i.customerGender,
      sumAssured: i.sumAssured,
      term: i.term,
      freq: i.freq,
      estPremium: i.premium + '.000',
      selectedRiders: i.selectedRiders,
      topUpAmount: i.topUpAmount,
      topUpStartYear: i.topUpStartYear,
      topUpDuration: i.topUpDuration,
      r5Mode: i.r5Mode,
    });
  };

  const handleEditIllustration = (i: CustomerIllustration) => {
    Alert.alert('Chỉnh sửa minh họa', `Mở minh họa "${i.proposalName}" ở chế độ chỉnh sửa?`, [
      { text: 'Hủy', style: 'cancel' },
      { text: 'Chỉnh sửa', onPress: () => handleViewIllustration(i) },
    ]);
  };

  const handleDownloadPDF = (i: CustomerIllustration) => {
    Alert.alert('Tải PDF', `Đã tải xuống ${i.proposalName} của ${i.customerName}`);
  };

  const handleCreateNew = () => {
    navigation.navigate('M06InputCustomerScreen', {
      productId: 'XUM',
      customerName: customer.name,
      customerAge: customer.age,
      customerGender: customer.gender,
    });
  };

  const handleEditCustomer = () => {
    setEditingCustomer({
      name: customer.name,
      occupation: customer.occupation,
      dob: customer.dob,
      gender: customer.gender,
      phone: customer.phone,
      email: customer.email,
      category: customer.category,
      dependentsCount: customer.dependentsCount,
      labels: customer.labels,
    });
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Icon name="arrow_back" size={20} color={colors.primary} />
            <Text style={styles.backBtnText}>Quay lại</Text>
          </TouchableOpacity>
          <Text style={styles.breadcrumb}>Quản lý khách hàng / Chi tiết</Text>
        </View>

        <View style={styles.profileCard}>
          <View style={[styles.avatar, { backgroundColor: customer.gender === 'Nữ' ? '#EC4899' : '#10B981' }]}>
            <Text style={styles.avatarText}>{customer.name.charAt(0)}</Text>
          </View>
          <View style={styles.profileInfo}>
            <View style={styles.profileHeader}>
              <Text style={styles.customerId}>Mã KH: {customer.id}</Text>
              <View style={styles.labelRow}>
                {customer.labels.map((lbl: string) => {
                  const meta = LABEL_META[lbl as CustomerLabel];
                  return (
                    <View key={lbl} style={[styles.labelTag, { backgroundColor: meta.bg }]}>
                      <Text style={[styles.labelTagText, { color: meta.color }]}>
                        ⭐ {meta.label}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
            <Text style={styles.name}>{customer.name}</Text>
            <Text style={styles.occupation}>{customer.occupation}</Text>
            <View style={styles.metaRow}>
              <View style={styles.metaItem}>
                <Icon name="calendar_today" size={14} color={colors.onSurfaceVariant} />
                <Text style={styles.metaText}>{customer.dob} ({customer.age} tuổi)</Text>
              </View>
              <View style={styles.metaItem}>
                <Icon name={customer.gender === 'Nữ' ? 'female' : 'male'} size={14} color={colors.onSurfaceVariant} />
                <Text style={styles.metaText}>{customer.gender}</Text>
              </View>
              <View style={styles.metaItem}>
                <Icon name="phone" size={14} color={colors.onSurfaceVariant} />
                <Text style={styles.metaText}>{customer.phone}</Text>
              </View>
            </View>
            <View style={styles.metaRow}>
              <View style={styles.metaItem}>
                <Icon name="mail" size={14} color={colors.onSurfaceVariant} />
                <Text style={styles.metaText}>{customer.email}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.statsRow}>
          <View style={[styles.statCard, { borderLeftColor: colors.primary }]}>
            <Text style={styles.statValue}>{customer.illustrations.length}</Text>
            <Text style={styles.statLabel}>Tài liệu minh họa</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: '#F59E0B' }]}>
            <Text style={styles.statValue}>{customer.notes.length}</Text>
            <Text style={styles.statLabel}>Ghi chú CRM</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: '#8B5CF6' }]}>
            <Text style={styles.statValue}>{customer.dependents.length}</Text>
            <Text style={styles.statLabel}>Người phụ thuộc</Text>
          </View>
        </View>

        <View style={styles.actionBar}>
          <TouchableOpacity style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={handleCreateNew}>
            <Icon name="add" size={18} color="#ffffff" />
            <Text style={styles.actionBtnTextPrimary}>Tạo minh họa mới</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtn, styles.actionBtnSecondary]} onPress={handleEditCustomer}>
            <Icon name="edit" size={18} color={colors.primary} />
            <Text style={styles.actionBtnTextSecondary}>Chỉnh sửa</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtn, styles.actionBtnSecondary]} onPress={() => setShowNoteModal(true)}>
            <Icon name="comment" size={18} color="#D97706" />
            <Text style={[styles.actionBtnTextSecondary, { color: '#D97706' }]}>Ghi chú</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtn, styles.actionBtnDanger]} onPress={() => setShowDeleteModal(true)}>
            <Icon name="delete" size={18} color="#DC2626" />
            <Text style={styles.actionBtnTextDanger}>Xóa</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.tabs}>
          {TABS.map((tab, idx) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === idx && styles.tabActive]}
              onPress={() => setActiveTab(idx)}
            >
              <Icon name={tab.icon} size={16} color={activeTab === idx ? colors.primary : colors.onSurfaceVariant} />
              <Text style={[styles.tabText, activeTab === idx && styles.tabTextActive]}>
                {tab.label}
                {tab.count !== null && ` (${tab.count})`}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {activeTab === 0 && (
          <View style={styles.tabContent}>
            <InfoSection title="Thông tin cá nhân">
              <InfoRow icon="person" label="Họ và tên" value={customer.name} />
              <InfoRow icon="male" label="Giới tính" value={customer.gender} />
              <InfoRow icon="calendar_today" label="Ngày sinh" value={`${customer.dob} (${customer.age} tuổi)`} />
              <InfoRow icon="work" label="Nghề nghiệp" value={customer.occupation} />
              <InfoRow icon="badge" label="CCCD" value={customer.idNumber || '—'} />
            </InfoSection>

            <InfoSection title="Thông tin liên hệ">
              <InfoRow icon="phone" label="Số điện thoại" value={customer.phone} />
              <InfoRow icon="mail" label="Email" value={customer.email} />
              <InfoRow icon="location_on" label="Địa chỉ" value={customer.address || '—'} />
            </InfoSection>

            <InfoSection title="Phân loại & Ghi chú">
              <InfoRow icon="category" label="Phân loại" value={customer.category === 'main' ? 'Bên mua bảo hiểm' : 'Người phụ thuộc'} />
              <View style={styles.labelsDisplay}>
                <Text style={styles.labelsLabel}>Nhãn:</Text>
                <View style={styles.labelRow}>
                  {customer.labels.map((lbl: string) => {
                    const meta = LABEL_META[lbl as CustomerLabel];
                    return (
                      <View key={lbl} style={[styles.labelTag, { backgroundColor: meta.bg }]}>
                        <Text style={[styles.labelTagText, { color: meta.color }]}>
                          ⭐ {meta.label}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>
              <InfoRow icon="info" label="Ngày tạo hồ sơ" value={customer.createdAt} />
            </InfoSection>
          </View>
        )}

        {activeTab === 1 && (
          <View style={styles.tabContent}>
            {customer.illustrations.length === 0 ? (
              <EmptyState icon="description" message="Chưa có tài liệu minh họa" />
            ) : (
              customer.illustrations.map((i: any) => {
                const stageMeta = STAGE_META[i.stage];
                return (
                  <View key={i.id} style={styles.illustrationCard}>
                    <View style={styles.illustrationHeader}>
                      <View style={styles.illustrationTitleCol}>
                        <Text style={styles.illustrationName}>{i.proposalName}</Text>
                        <Text style={styles.illustrationId}>Mã: {i.id}</Text>
                      </View>
                      <View style={[styles.stageBadge, { backgroundColor: stageMeta.bg }]}>
                        <Text style={[styles.stageBadgeText, { color: stageMeta.color }]}>
                          {stageMeta.label}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.illustrationStats}>
                      <View style={styles.illustrationStat}>
                        <Text style={styles.statValue}>{i.premium}</Text>
                        <Text style={styles.statLabel}>Phí BH (VNĐ)</Text>
                      </View>
                      <View style={styles.illustrationStat}>
                        <Text style={styles.statValue}>{i.sumAssured}</Text>
                        <Text style={styles.statLabel}>STBH (đ)</Text>
                      </View>
                      <View style={styles.illustrationStat}>
                        <Text style={styles.statValue}>{i.term}</Text>
                        <Text style={styles.statLabel}>Thời hạn</Text>
                      </View>
                      <View style={styles.illustrationStat}>
                        <Text style={styles.statValue}>{i.selectedRiders.length}</Text>
                        <Text style={styles.statLabel}>Sản phẩm bổ trợ</Text>
                      </View>
                    </View>

                    <View style={styles.illustrationDates}>
                      <Text style={styles.illustrationDateLabel}>Cập nhật: {i.lastUpdated}</Text>
                      <Text style={styles.illustrationDateLabel}>Hết hạn: {i.expiryDate}</Text>
                    </View>

                    <View style={styles.illustrationActions}>
                      <TouchableOpacity style={[styles.illustBtn, styles.illustBtnView]} onPress={() => handleViewIllustration(i)}>
                        <Icon name="visibility" size={16} color={colors.primary} />
                        <Text style={[styles.illustBtnText, { color: colors.primary }]}>Xem</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={[styles.illustBtn, styles.illustBtnEdit]} onPress={() => handleEditIllustration(i)}>
                        <Icon name="edit" size={16} color="#D97706" />
                        <Text style={[styles.illustBtnText, { color: '#D97706' }]}>Sửa</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={[styles.illustBtn, styles.illustBtnPdf]} onPress={() => handleDownloadPDF(i)}>
                        <Icon name="picture_as_pdf" size={16} color="#EF4444" />
                        <Text style={[styles.illustBtnText, { color: '#EF4444' }]}>PDF</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })
            )}
          </View>
        )}

        {activeTab === 2 && (
          <View style={styles.tabContent}>
            {customer.notes.length === 0 ? (
              <EmptyState icon="comment" message="Chưa có ghi chú" />
            ) : (
              customer.notes.map((n: any) => {
                const meta = NOTE_TYPE_META[n.type as keyof typeof NOTE_TYPE_META] || NOTE_TYPE_META.journal;
                return (
                  <View key={n.id} style={[styles.noteCard, { borderLeftColor: meta.color }]}>
                    <View style={styles.noteHeader}>
                      <View style={[styles.noteTypeBadge, { backgroundColor: meta.bg }]}>
                        <Icon name={meta.icon} size={14} color={meta.color} />
                        <Text style={[styles.noteTypeText, { color: meta.color }]}>{meta.label}</Text>
                      </View>
                      <Text style={styles.noteDate}>{n.date}</Text>
                    </View>
                    <Text style={styles.noteContent}>{n.content}</Text>
                    <Text style={styles.noteAuthor}>— {n.author}</Text>
                  </View>
                );
              })
            )}
          </View>
        )}

        {activeTab === 3 && (
          <View style={styles.tabContent}>
            {customer.dependents.length === 0 ? (
              <EmptyState icon="groups" message="Chưa có người phụ thuộc" />
            ) : (
              customer.dependents.map((d: any) => (
                <View key={d.id} style={styles.dependentCard}>
                  <View style={[styles.dependentAvatar, { backgroundColor: d.gender === 'Nữ' ? '#EC4899' : '#10B981' }]}>
                    <Text style={styles.dependentAvatarText}>{d.name.charAt(0)}</Text>
                  </View>
                  <View style={styles.dependentInfo}>
                    <View style={styles.dependentNameRow}>
                      <Text style={styles.dependentName}>{d.name}</Text>
                      <View style={styles.relationshipTag}>
                        <Text style={styles.relationshipTagText}>{RELATIONSHIP_LABEL[d.relationship]}</Text>
                      </View>
                    </View>
                    <Text style={styles.dependentMeta}>
                      {d.dob} ({d.age} tuổi) • {d.gender}
                    </Text>
                    {d.occupation && <Text style={styles.dependentOcc}>Nghề: {d.occupation}</Text>}
                  </View>
                </View>
              ))
            )}
          </View>
        )}
      </ScrollView>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <EditModal
          visible
          title={`Chỉnh sửa: ${editingCustomer.name}`}
          subtitle="Cập nhật thông tin khách hàng"
          fields={buildCustomerEditFields(editingCustomer, setEditingCustomer)}
          onCancel={() => setEditingCustomer(null)}
          onSave={async () => {
            await customerRepository.update(customer.id, editingCustomer);
            Alert.alert('Thành công', `Đã cập nhật thông tin ${editingCustomer.name}`);
            setEditingCustomer(null);
            setCustomer((prev) => prev ? { ...prev, ...editingCustomer } : prev);
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      {showDeleteModal && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          title="Xóa khách hàng"
          customerName={customer.name}
          message={`Bạn có chắc chắn muốn xóa hồ sơ khách hàng "${customer.name}"? Hành động này không thể hoàn tác.`}
          confirmText="Xóa khách hàng"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            await customerRepository.delete(customer.id);
            setShowDeleteModal(false);
            Alert.alert('Thành công', 'Đã xóa khách hàng.');
            navigation.goBack();
          }}
        />
      )}

      {/* Quick Note Modal */}
      {showNoteModal && (
        <QuickNoteModal
          visible={showNoteModal}
          customerId={customer.id}
          customerName={customer.name}
          onClose={() => setShowNoteModal(false)}
          onSaved={() => {
            noteRepository.getByCustomerId(customerId).then(setRepoNotes);
          }}
        />
      )}
    </View>
  );
};

const InfoSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <View style={styles.infoSection}>
    <Text style={styles.infoSectionTitle}>{title}</Text>
    <View style={styles.infoSectionContent}>{children}</View>
  </View>
);

const InfoRow: React.FC<{ icon: any; label: string; value: string }> = ({ icon, label, value }) => (
  <View style={styles.infoRow}>
    <View style={styles.infoRowLabel}>
      <Icon name={icon} size={16} color={colors.onSurfaceVariant} />
      <Text style={styles.infoRowLabelText}>{label}</Text>
    </View>
    <Text style={styles.infoRowValue}>{value}</Text>
  </View>
);

const EmptyState: React.FC<{ icon: any; message: string }> = ({ icon, message }) => (
  <View style={styles.emptyState}>
    <Icon name={icon} size={48} color={colors.onSurfaceVariant} />
    <Text style={styles.emptyStateText}>{message}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F9FAFB' },
  scrollContent: { padding: spacing[5], paddingBottom: 100, gap: spacing[4] },
  notFound: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing[3], padding: spacing[5] },
  notFoundText: { fontSize: 14, color: colors.onSurfaceVariant },

  header: { gap: spacing[2] },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-start' },
  backBtnText: { fontSize: 13, color: colors.primary, fontWeight: fontWeights.medium },
  breadcrumb: { fontSize: 12, color: colors.onSurfaceVariant },

  profileCard: {
    flexDirection: 'row', backgroundColor: '#ffffff', borderRadius: borderRadius.md,
    padding: spacing[4], borderWidth: 1, borderColor: colors.outlineVariant, gap: spacing[3],
  },
  avatar: { width: 64, height: 64, borderRadius: 32, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 24, fontWeight: fontWeights.bold, color: '#ffffff' },
  profileInfo: { flex: 1, gap: spacing[1] },
  profileHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing[2], flexWrap: 'wrap' },
  customerId: {
    fontSize: 11, color: colors.onSurfaceVariant, fontWeight: fontWeights.medium,
    backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4,
  },
  name: { fontSize: 18, fontWeight: fontWeights.bold, color: colors.onSurface, marginTop: 2 },
  occupation: { fontSize: 13, color: colors.onSurfaceVariant },
  metaRow: { flexDirection: 'row', gap: spacing[3], flexWrap: 'wrap', marginTop: 4 },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  metaText: { fontSize: 12, color: colors.onSurface },

  statsRow: { flexDirection: 'row', gap: spacing[2] },
  statCard: {
    flex: 1, backgroundColor: '#ffffff', borderRadius: borderRadius.md,
    padding: spacing[3], borderLeftWidth: 3, borderColor: colors.outlineVariant, alignItems: 'flex-start',
  },
  statValue: { fontSize: 20, fontWeight: fontWeights.bold, color: colors.onSurface },
  statLabel: { fontSize: 11, color: colors.onSurfaceVariant, marginTop: 2 },

  actionBar: { flexDirection: 'row', gap: spacing[2] },
  actionBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: spacing[3], borderRadius: borderRadius.md, gap: spacing[2],
  },
  actionBtnPrimary: { backgroundColor: colors.primary },
  actionBtnTextPrimary: { color: '#ffffff', fontSize: 13, fontWeight: fontWeights.bold },
  actionBtnSecondary: { backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: colors.primary },
  actionBtnTextSecondary: { color: colors.primary, fontSize: 13, fontWeight: fontWeights.bold },
  actionBtnDanger: { backgroundColor: '#FEF2F2', borderWidth: 1, borderColor: '#DC2626' },
  actionBtnTextDanger: { color: '#DC2626', fontSize: 13, fontWeight: fontWeights.bold },

  tabs: { flexDirection: 'row', backgroundColor: '#ffffff', borderRadius: borderRadius.md, padding: 4, gap: 4 },
  tab: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: spacing[2], borderRadius: borderRadius.sm, gap: 4,
  },
  tabActive: { backgroundColor: '#EFF6FF' },
  tabText: { fontSize: 12, color: colors.onSurfaceVariant, fontWeight: fontWeights.medium },
  tabTextActive: { color: colors.primary, fontWeight: fontWeights.bold },

  tabContent: { gap: spacing[3] },

  infoSection: {
    backgroundColor: '#ffffff', borderRadius: borderRadius.md, padding: spacing[4],
    borderWidth: 1, borderColor: colors.outlineVariant,
  },
  infoSectionTitle: {
    fontSize: 13, fontWeight: fontWeights.bold, color: colors.primary,
    textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: spacing[3],
  },
  infoSectionContent: { gap: spacing[2] },
  infoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 },
  infoRowLabel: { flexDirection: 'row', alignItems: 'center', gap: spacing[2] },
  infoRowLabelText: { fontSize: 12, color: colors.onSurfaceVariant },
  infoRowValue: { fontSize: 13, color: colors.onSurface, fontWeight: fontWeights.medium, maxWidth: '60%', textAlign: 'right' },

  labelsDisplay: { gap: spacing[1] },
  labelsLabel: { fontSize: 12, color: colors.onSurfaceVariant },

  illustrationCard: {
    backgroundColor: '#ffffff', borderRadius: borderRadius.md, padding: spacing[4],
    borderWidth: 1, borderColor: colors.outlineVariant, gap: spacing[3],
  },
  illustrationHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  illustrationTitleCol: { flex: 1, gap: 2 },
  illustrationName: { fontSize: 13, fontWeight: fontWeights.bold, color: colors.onSurface },
  illustrationId: { fontSize: 11, color: colors.onSurfaceVariant },
  stageBadge: { paddingVertical: 3, paddingHorizontal: 8, borderRadius: borderRadius.full },
  stageBadgeText: { fontSize: 10, fontWeight: fontWeights.bold },

  illustrationStats: { flexDirection: 'row', gap: spacing[2] },
  illustrationStat: { flex: 1, alignItems: 'flex-start', gap: 2 },
  illustrationDates: { flexDirection: 'row', justifyContent: 'space-between' },
  illustrationDateLabel: { fontSize: 11, color: colors.onSurfaceVariant },

  illustrationActions: { flexDirection: 'row', gap: spacing[2] },
  illustBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: spacing[2], borderRadius: borderRadius.sm, gap: 4,
  },
  illustBtnView: { backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: colors.primary },
  illustBtnEdit: { backgroundColor: '#FFF7ED', borderWidth: 1, borderColor: '#F59E0B' },
  illustBtnPdf: { backgroundColor: '#FEF2F2', borderWidth: 1, borderColor: '#EF4444' },
  illustBtnText: { fontSize: 12, fontWeight: fontWeights.bold },

  noteCard: {
    backgroundColor: '#ffffff', borderRadius: borderRadius.md, padding: spacing[3],
    borderWidth: 1, borderColor: colors.outlineVariant, borderLeftWidth: 3, gap: spacing[2],
  },
  noteHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  noteTypeBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 2, paddingHorizontal: 8, borderRadius: borderRadius.full },
  noteTypeText: { fontSize: 11, fontWeight: fontWeights.bold },
  noteDate: { fontSize: 11, color: colors.onSurfaceVariant },
  noteContent: { fontSize: 13, color: colors.onSurface, lineHeight: 20 },
  noteAuthor: { fontSize: 11, color: colors.onSurfaceVariant, fontStyle: 'italic' },

  dependentCard: {
    flexDirection: 'row', backgroundColor: '#ffffff', borderRadius: borderRadius.md,
    padding: spacing[3], borderWidth: 1, borderColor: colors.outlineVariant, gap: spacing[3],
    alignItems: 'center',
  },
  dependentAvatar: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center' },
  dependentAvatarText: { fontSize: 18, fontWeight: fontWeights.bold, color: '#ffffff' },
  dependentInfo: { flex: 1, gap: 2 },
  dependentNameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing[2] },
  dependentName: { fontSize: 14, fontWeight: fontWeights.bold, color: colors.onSurface },
  relationshipTag: { backgroundColor: '#EEF2FF', paddingVertical: 2, paddingHorizontal: 6, borderRadius: borderRadius.full },
  relationshipTagText: { fontSize: 10, color: '#4338CA', fontWeight: fontWeights.semibold },
  dependentMeta: { fontSize: 12, color: colors.onSurfaceVariant },
  dependentOcc: { fontSize: 11, color: colors.onSurfaceVariant },

  emptyState: { alignItems: 'center', justifyContent: 'center', padding: spacing[10], gap: spacing[2] },
  emptyStateText: { fontSize: 13, color: colors.onSurfaceVariant },

  labelRow: { flexDirection: 'row', gap: 4, flexWrap: 'wrap' },
  labelTag: { paddingVertical: 2, paddingHorizontal: 8, borderRadius: borderRadius.full },
  labelTagText: { fontSize: 10, fontWeight: fontWeights.semibold },
});